import React, { useState, useMemo, useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Link, useHistory, useLocation } from 'react-router-dom'
import { isEmpty, truncate, isEqual, toArray, isNil, startCase } from 'lodash'
import {
  IconCircleCheckFilled,
  IconCircleDashedCheck,
  IconTrash,
  IconReplace,
} from '@tabler/icons-react'

import queryString from 'query-string'

import { Loader } from '@/components/loaders/Loader'
import { PageHeader } from '@/components/headers/PageHeader'
import {
  deletePlaylistCall,
  fetchPlaylist,
  patchPlaylist,
  patchPlaylistCall,
  fetchSummariesForCallIds,
} from '@/reducers/playlists/playlists.actions'
import { pauseMediaPlayer } from '@/reducers/media/media.redux'
import { AdvancedTable } from '@/components/tables/AdvancedTable/AdvancedTable'
import { formatTime, reorder, shouldShowOmniPromotion } from '@/utils/helpers'
import { ButtonAndFormModal } from '@/components/layout/modals/ButtonAndFormModal'
import rocketIcon from '@/assets/images/rocket.svg'
import { ModalActions } from '@/components/modals/ModalActions'
import { MediaDrawer } from '@/components/media/MediaDrawer'
import { CallComments } from '@/views/Calls/components/CallComments'
import NoData from '@/components/NoData'
import { PLAYLISTS_TABLE_NAME } from '@/reducers/customColumns/customColumns.constants'
import { DEFAULT_CALL_COLUMNS } from '@/views/Calls/callColumns'
import {
  getMemoizedColumns,
  getDynamicColumns,
  getCompleteColumns,
  getCompleteColumnsWithScores,
  filterExtraColumns,
  getChildColumns,
  getScorecardsCustomColumns,
} from '@/reducers/customColumns/customColumns.helpers'
import { getChannelTabIndex } from '@/reducers/callSearch/helpers'
import {
  auditColumns,
  CHANNEL_TYPE,
  channelTabIndexMap,
  CHANNEL_NAME,
  CHANNEL_NAME_PLURAL,
} from '@/utils/constants'
import { CHANNEL_ICONS } from '@/utils/channelIcons'
import { CustomColumns } from '@/components/tables/CustomColumns/CustomColumns'
import { Breadcrumbs } from '@/components/forms/Breadcrumbs/Breadcrumbs'
import { CreateOrUpdatePlaylistButtonAndModal } from '@/views/Playlists/components/CreateOrUpdatePlaylistButtonAndModal'
import { Menu } from '@/components/navigation/Menu'
import { OmnichannelPromotionButton } from '@/components/promotions/OmnichannelPromotionButton'
import { Button } from '@/components/buttons/Button'
import { Card } from '@/components/cards/Card'
import { InteractiveTooltip } from '@/components/tooltips/InteractiveTooltip'

import { PlaylistName } from './PlaylistName'
import { EditPlaylistCallAlias } from './EditPlaylistCallAlias'

const getRecordType = (item) => {
  return (item && item.record_type) || CHANNEL_TYPE.CALL
}

const PlaylistItemsContent = () => {
  const dispatch = useDispatch()
  const history = useHistory()
  const [rowIdsRendered, setRowIdsRendered] = useState([])
  const { playlist, playlistChanges, loading } = useSelector((state) => state.playlists)
  const { customColumns } = useSelector((state) => state.customColumns)
  const tableIndex = 'call_id'
  const savedCustomColumns = customColumns[PLAYLISTS_TABLE_NAME]
  const currentOrganization = useSelector((state) => state.currentOrganization)
  const { organizationid: currentUserOrgId } = useSelector((state) => state.currentUser)
  const isBaltoAdmin = currentUserOrgId === 1
  const { search } = useLocation()
  const parsedSearch = useMemo(() => queryString.parse(search), [search])
  const { organization_id: requestedOrgId, channel: requestedChannel } = parsedSearch
  const organizations = useSelector((state) => state.organizations)
  const targetOrganization = organizations.find((org) => org.id === Number(requestedOrgId))

  // Taking first item type from playlist to determine initial channel type if not specified in URL
  const defaultChannel =
    !requestedChannel &&
    !isEmpty(playlist?.playlist_calls) &&
    getRecordType(playlist.playlist_calls[0])
  const defaultChannelTabIndex = getChannelTabIndex(requestedChannel || defaultChannel)
  const [channelTabIndex, setChannelTabIndex] = useState(defaultChannelTabIndex)
  const channel = channelTabIndexMap[channelTabIndex]

  const counts = useMemo(() => {
    const items = playlist?.playlist_calls || []
    return items.reduce(
      (acc, item) => {
        switch (item.record_type) {
          case CHANNEL_TYPE.CALL:
            return { ...acc, calls: acc.calls + 1 }
          case CHANNEL_TYPE.EMAIL:
            return { ...acc, emails: acc.emails + 1 }
          case CHANNEL_TYPE.SMS:
            return { ...acc, SMS: acc.SMS + 1 }
          case CHANNEL_TYPE.CHAT:
            return { ...acc, chats: acc.chats + 1 }
          default:
            return acc
        }
      },
      { calls: 0, emails: 0, SMS: 0, chats: 0 }
    )
  }, [playlist])

  const tabDefinitions = [
    { key: 'calls', channel: CHANNEL_TYPE.CALL, index: 0 },
    { key: 'emails', channel: CHANNEL_TYPE.EMAIL, index: 1 },
    { key: 'SMS', channel: CHANNEL_TYPE.SMS, index: 2 },
    { key: 'chats', channel: CHANNEL_TYPE.CHAT, index: 3 },
  ]

  const handleChannelChange = (activeIndex) => {
    if (activeIndex === channelTabIndex) {
      return
    }
    const nextChannel = channelTabIndexMap[activeIndex]
    setChannelTabIndex(activeIndex)
    history.push({ search: `?${queryString.stringify({ ...parsedSearch, channel: nextChannel })}` })
  }

  const channelTabItems = tabDefinitions.map(({ key, channel: tabChannel, index }) => {
    const ChannelIcon = CHANNEL_ICONS[tabChannel]
    return {
      accessor: tabChannel,
      icon: <ChannelIcon />,
      label: CHANNEL_NAME_PLURAL[tabChannel],
      active: channelTabIndex === index,
      onClick: () => handleChannelChange(index),
      badge: counts[key] > 0 ? counts[key] : undefined,
    }
  })

  useEffect(() => {
    const searchTabIndex = getChannelTabIndex(requestedChannel || defaultChannel)
    if (searchTabIndex !== channelTabIndex) {
      setChannelTabIndex(searchTabIndex)
    }
  }, [defaultChannel, requestedChannel, channelTabIndex])

  const updateRowIdsRendered = (rowIds) => {
    if (!isEqual(rowIds, rowIdsRendered)) {
      setRowIdsRendered(rowIds)
    }
  }

  const handleReorder = async (result, activePage, rowsPerPage) => {
    const { source, destination } = result
    if (!destination) return

    const numberOfRowsToOffset = activePage * rowsPerPage - rowsPerPage
    const offset = numberOfRowsToOffset

    const channelItems = playlist.playlist_calls?.filter((item) => item.record_type === channel)
    if (isEmpty(channelItems)) return

    const reorderedChannelItems =
      activePage === 1
        ? reorder(channelItems, source.index, destination.index)
        : reorder(channelItems, offset + source.index, offset + destination.index)

    const changedItems = reorderedChannelItems
      .map((item, index) => {
        if (item.order_id !== index) {
          return {
            call_id: item.call_id,
            is_flagged: item.is_flagged,
            order_id: index,
          }
        }
        return null
      })
      .filter(Boolean)

    if (!isEmpty(changedItems)) {
      await dispatch(
        patchPlaylist(playlist.uuid, playlist.organization_id, { playlist_calls: changedItems })
      )
      await dispatch(fetchPlaylist(playlist.uuid, playlist.organization_id))
    }
  }

  const handleRemoveCallFromPlaylist = (callId) => {
    dispatch(deletePlaylistCall(playlist.uuid, playlist.organization_id, callId))
  }

  const handleToggleFlagged = (row) => {
    dispatch(
      patchPlaylistCall(
        playlist.uuid,
        playlist.organization_id,
        row.call_id,
        { is_flagged: !row.is_flagged },
        true
      )
    )
  }

  const handleEditAlias = (value, call) => {
    dispatch(
      patchPlaylistCall(
        playlist.uuid,
        playlist.organization_id,
        call.call_id,
        { alias: value },
        true
      )
    )
  }

  const pauseAudio = () => {
    dispatch(pauseMediaPlayer())
  }

  const { callId: callIdListeningTo, progress } = useSelector((state) => state.media.mediaPlayer)

  const generateCallExplorerLink = (callId) => {
    return `/calls/${callId}?playlistUuid=${playlist.uuid}${callId === callIdListeningTo ? `&timestamp=${progress}&autoplay=true` : ''}`
  }
  const generateMessageExplorerLink = (messageId) => {
    return `/messages/${messageId}?playlistUuid=${playlist.uuid}`
  }

  const trimText = (text) => {
    return truncate(text, { length: 50, omission: '...' })
  }

  const buildChangeString = (change) => {
    if (!isEmpty(change.calls_added)) {
      return `Added: ${change.calls_added.join(', ')}`
    }
    if (!isEmpty(change.calls_removed)) {
      return `Removed: ${change.calls_removed.join(', ')}`
    }
    if (!isEmpty(change.name)) {
      return `Renamed this to: ${change.name}`
    }
    if (!isEmpty(change.calls_updated)) {
      return change.calls_updated.map((obj) => {
        const [key, value] = Object.entries(obj)[0]

        let valueStr = value
        if (value === 'Flagged') {
          valueStr = 'Reviewed'
        }
        if (value === 'Removed flag') {
          valueStr = 'Removed review'
        }
        return `${valueStr}: ${key}`
      })
    }
    return ''
  }

  const playlistChangesRows = playlistChanges?.map((change) => {
    const displayChanges = buildChangeString(change)

    const eventType =
      change.event_type === 'update_playlist_calls' ? 'Update Interactions' : 'Update Playlist'

    return {
      ...change,
      created_at: formatTime(change.created_at),
      event_type: eventType,
      what_changed: {
        as: (
          <div>
            <InteractiveTooltip
              interactive
              hide={displayChanges.length < 50}
              content={displayChanges}
            >
              <div>{trimText(displayChanges)}</div>
            </InteractiveTooltip>
          </div>
        ),
        value: displayChanges,
      },
      calls_added: !isEmpty(change.calls_added) ? change.calls_added.join(', ') : 'N/A',
      calls_removed: !isEmpty(change.calls_removed) ? change.calls_removed.join(', ') : 'N/A',
      calls_updated: !isEmpty(change.calls_updated) ? change.calls_updated.join(', ') : 'N/A',
    }
  })

  const actions = [
    {
      label: 'Delete',
      trigger: (row) => {
        return (
          <ButtonAndFormModal
            popup
            icon={<IconTrash />}
            buttonProps={{ basic: true, small: true, square: true }}
            modalId={`playlists/delete/calls/${row.call_id}`}
            modalTitle={`Remove ${startCase(row.record_type)} `}
            modalProps={{ size: 'tiny' }}
            form={
              <ModalActions
                onSubmit={() => handleRemoveCallFromPlaylist(row.call_id)}
                buttonLabel={`Remove ${startCase(row.record_type)}`}
                isDelete
              >
                <p>{`Are you sure you want to remove this ${row.record_type}? This action cannot be undone.`}</p>
              </ModalActions>
            }
          />
        )
      },
    },
  ]

  const DEFAULT_COLUMNS = [
    {
      accessor: 'call_identifier',
      label: 'Identifier',
      isSortable: true,
      is_locked: true,
      sticky: true,
      isSearchable: true,
    },
    {
      accessor: 'is_flagged',
      label: 'Status',
      isSortable: false,
      is_locked: true,
      isSearchable: true,
      searchSchema: {
        label: 'Coaching Status',
        type: 'select',
        options: [
          { label: 'Reviewed', value: true },
          { label: 'Not Reviewed', value: false },
        ],
      },
    },
    {
      accessor: 'comments',
      label: 'Comments',
      isSortable: false,
    },
    ...DEFAULT_CALL_COLUMNS.filter((column) => column.accessor !== 'start_time').filter(
      (column) => column.accessor !== 'coaching_comments'
    ),
    ...DEFAULT_CALL_COLUMNS.filter((column) => column.accessor === 'start_time').map((column) => ({
      ...column,
      isSearchable: true,
      searchSchema: { label: 'Date', type: 'date' },
    })),
  ]

  // TODO: Refactor this into one function instead of several useMemos that all claim the function is the "final complete columns"
  // TODO: Reduce duplication of code (completeColumns, completeColumnsWithScores) - (dynamicColumns, dynamicScorecardColumns)
  // TODO: Reduce duplication between this file and CallSearchPage
  const formattedScorecardColumns = useMemo(() => {
    const scorecardColumns = []
    const uniqueScorecards = new Set()

    playlist?.playlist_calls.forEach((call) => {
      if (call.scorecards) {
        call.scorecards.forEach((card) => {
          if (!uniqueScorecards.has(card.scorecard_sid)) {
            uniqueScorecards.add(card.scorecard_sid)
            scorecardColumns.push({
              sid: card.scorecard_sid,
              label: card.scorecard_name,
              value: card.scorecard_sid,
              scorecardId: card.scorecard_id,
            })
          } else {
            const diffNameSameScorecard = scorecardColumns.filter(
              (obj) =>
                uniqueScorecards.has(obj.sid) &&
                obj.label !== card.scorecard_name &&
                obj.scorecardId < card.scorecard_id &&
                obj.sid === card.scorecard_sid
            )
            if (!isEmpty(diffNameSameScorecard)) {
              diffNameSameScorecard[0].label = card.scorecard_name
            }
          }
        })
      }
    })
    return scorecardColumns
  }, [playlist?.playlist_calls])

  const dynamicScorecardColumns = useMemo(() => {
    if (!playlist?.playlist_calls) {
      return []
    }
    return getScorecardsCustomColumns(formattedScorecardColumns)
  }, [playlist?.playlist_calls])

  const columnsByCallType = useMemo(() => {
    const visibility = (col) => {
      if (!col.hiddenFor) col.hiddenFor = () => false
      const hiddenForChannel = col.hiddenFor(channel)

      const is_hidden = Boolean(col.is_hidden) || hiddenForChannel

      return { ...col, is_hidden }
    }

    return DEFAULT_COLUMNS.map(visibility).filter((col) => !col.is_hidden)
  }, [rowIdsRendered, savedCustomColumns, playlist?.playlist_calls, channel])

  const columns = useMemo(
    () => getMemoizedColumns(savedCustomColumns, columnsByCallType),
    [savedCustomColumns, columnsByCallType]
  )

  const dynamicColumns = useMemo(
    () => getDynamicColumns(playlist?.playlist_calls),
    [columns, playlist?.playlist_calls, rowIdsRendered, savedCustomColumns]
  )

  const completeColumns = getCompleteColumns(columns, dynamicColumns)

  const completeColumnsWithScores = getCompleteColumnsWithScores(
    completeColumns,
    dynamicScorecardColumns
  )

  const extraColumns = getChildColumns(completeColumnsWithScores, savedCustomColumns)

  useEffect(() => {
    if (!isEmpty(rowIdsRendered)) {
      const callsRenderedWithoutSummariesLoaded = toArray(playlist?.playlist_calls)
        .filter((call) => rowIdsRendered.includes(call.call_id)) // Only fetches summaries for calls that have been rendered
        .filter(({ call_summary }) => call_summary === undefined)
        .map(({ call_id }) => call_id)

      if (!isEmpty(callsRenderedWithoutSummariesLoaded)) {
        dispatch(fetchSummariesForCallIds(callsRenderedWithoutSummariesLoaded))
      }
    }
  }, [rowIdsRendered, savedCustomColumns, playlist?.playlist_calls])

  const filteredColumns = useMemo(
    () => filterExtraColumns(completeColumnsWithScores, savedCustomColumns),
    [columns, rowIdsRendered, savedCustomColumns, dynamicColumns]
  )

  // END TODO

  const activeChannels = isBaltoAdmin
    ? targetOrganization?.active_channels
    : currentOrganization.active_channels
  const showOmniPromotion = shouldShowOmniPromotion(activeChannels, channel, isBaltoAdmin)
  const noDataHeader = showOmniPromotion ? (
    <>
      <img alt="Rocket icon - omni-channel promotion" className="mb" src={rocketIcon} />
      <div>{`Balto now supports ${CHANNEL_NAME[channel]} conversations!`}</div>
    </>
  ) : (
    `No ${CHANNEL_NAME_PLURAL[channel]} in this playlist`
  )
  const noDataText = showOmniPromotion ? (
    <>
      {`View, analyze, and coach customer ${CHANNEL_NAME_PLURAL[channel].toLowerCase()} right alongside your calls - all in one place.`}
      <br />
      Ask your admin to enable Omnichannel to get started
      <OmnichannelPromotionButton />
    </>
  ) : (
    `Begin by adding ${CHANNEL_NAME_PLURAL[channel]} to your new playlist.`
  )

  const renderPlaylistPage = () => {
    if (loading.playlist || isEmpty(playlist)) {
      return (
        <>
          <PageHeader
            isDataGrid
            title="Playlist"
            data-testid={loading.playlist ? 'playlist-page-loading' : 'playlist-page-no-data'}
          />
          <div className="empty-table wide">
            {loading.playlist ? <Loader size="large" centered /> : <NoData />}
          </div>
        </>
      )
    }

    const rows = playlist?.playlist_calls
      ?.filter((item) => getRecordType(item) === channel)
      .map((call) => ({
        ...call,
        ...call.scorecards?.reduce((acc, scorecard) => {
          const scorecardColumn = formattedScorecardColumns.find(
            (col) => col.sid === scorecard.scorecard_sid
          )
          if (!isNil(scorecardColumn)) {
            acc[`${scorecardColumn.label} - Overall QA Score`] = scorecard.qa_score
          } else {
            acc[`${scorecard.scorecard_name} - Overall QA Score`] = scorecard.qa_score
          }
          return acc
        }, {}),
        call_identifier: {
          cellProps: {
            className: 'collapsing',
          },
          value:
            call.alias || (call.record_type === CHANNEL_TYPE.CALL ? call.call_id : call.message_id),
          as: (
            <EditPlaylistCallAlias
              key={call.record_type === CHANNEL_TYPE.CALL ? call.call_id : call.message_id}
              playlistUuid={playlist.uuid}
              call={call}
              handleEditAlias={handleEditAlias}
              generateCallExplorerLink={
                call.record_type === CHANNEL_TYPE.CALL
                  ? generateCallExplorerLink
                  : generateMessageExplorerLink
              }
              pauseAudio={pauseAudio}
              callType={call.record_type}
            />
          ),
        },
        is_flagged: {
          cellProps: {
            className: 'collapsing compact-cell',
          },
          value: call.is_flagged,
          as: (
            <Button
              square
              basic
              small
              onClick={() => handleToggleFlagged(call)}
              tooltip={call.is_flagged ? 'Remove Reviewed Status' : 'Mark as Reviewed'}
            >
              {call.is_flagged ? (
                <IconCircleCheckFilled className="icon-svg-large fill-success" />
              ) : (
                <IconCircleDashedCheck className="icon-svg-large fill-muted" />
              )}
            </Button>
          ),
        },
        comments: {
          value: call.callComments,
          as: (
            <CallComments
              comments={call.callComments || []}
              callId={call.call_id}
              recordType={call.record_type}
              messageId={call.message_id}
              context="playlist"
              title={`Comments: ${call.alias || (call.record_type === CHANNEL_TYPE.CALL ? call.call_id : call.message_id)}`}
            />
          ),
        },
      }))

    return (
      <>
        <PageHeader
          isDataGrid
          data-testid="playlist-page"
          breadcrumbs={
            <Breadcrumbs
              backLinks={[
                { label: 'Playlists', link: '/playlists', dataTestId: 'breadcrumb-playlists' },
              ]}
              currentLink={
                <PlaylistName loading={loading.playlist} playlist={playlist} uuid={playlist.uuid} />
              }
            />
          }
          actions={
            <>
              <CreateOrUpdatePlaylistButtonAndModal
                action="update"
                loading={loading.playlist}
                organization={isBaltoAdmin ? targetOrganization : currentOrganization}
              />

              <CustomColumns
                tableName={PLAYLISTS_TABLE_NAME}
                defaultColumns={columnsByCallType}
                extraColumns={extraColumns}
              />
              {!isEmpty(playlist?.playlist_calls) && !isEmpty(playlistChangesRows) && (
                <ButtonAndFormModal
                  buttonLabel="Change History"
                  icon={<IconReplace />}
                  modalId={`playlists/audit/${playlist.uuid}`}
                  modalProps={{ size: 'large' }}
                  modalTitle="Change History"
                  modalContent={
                    <Card className="no-padding">
                      <AdvancedTable
                        loading={loading.playlist}
                        rows={playlistChangesRows}
                        columns={auditColumns.filter((col) => col.accessor !== 'playlists')}
                        pagination
                        defaultOrder="desc"
                        sortEnabled={false}
                      />
                    </Card>
                  }
                />
              )}
            </>
          }
        />
        <header>
          <div className="call-search-tab">
            <Menu menuStyle="bar" items={channelTabItems} />
          </div>
        </header>

        <AdvancedTable
          noDataHeader={noDataHeader}
          noDataText={noDataText}
          noDataAction={
            !showOmniPromotion && (
              <Link to="/explorer" className="balto-button brand">
                {`Explore ${CHANNEL_NAME_PLURAL[channel]}`}
              </Link>
            )
          }
          dataGrid
          index={tableIndex}
          loading={loading.playlist}
          rows={rows}
          columns={filteredColumns}
          actions={actions}
          stickyAction
          pagination
          reorderable
          handleReorder={handleReorder}
          rowsPerPage={30}
          updateRowIdsRendered={updateRowIdsRendered}
        />

        <MediaDrawer />
      </>
    )
  }

  return renderPlaylistPage()
}

export const PlaylistItems = PlaylistItemsContent
